import axios from 'axios';
import { getConfig } from '../config';
import type {
  SkinSeoulAdminProduct,
  PublicationStatus,
  SkinSeoulProductType,
  StockStatus,
  CpProductStatus,
} from '../models/ProductTypes';

interface ProductsResponse {
  products: SkinSeoulAdminProduct[];
  total: number;
  page: number;
  limit: number;
}

export interface CategoryTerm {
  termId: number;
  name: string;
  slug: string;
  taxonomy: string;
  parentTermId?: number;
  thumbnailImageUrl?: string;
}

export interface BrandTerm {
  termId: number;
  name: string;
  slug: string;
  taxonomy: string;
  thumbnailImageUrl?: string;
}

export class SkinSeoulProductsClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getConfig().apiUrl;
  }

  async getAllProducts(
    page = 1,
    limit = 20,
    postStatus?: PublicationStatus,
    productType?: SkinSeoulProductType,
    stockStatus?: StockStatus,
    wave?: string,
    brandIds?: string,
    categoryIds?: string,
    cpProductStatus?: CpProductStatus,
    cpLastScrapedAt?: string,
    name?: string,
    gpi?: string,
  ): Promise<ProductsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (postStatus) {
      params.append('publicationStatus', postStatus);
    }

    if (productType) {
      params.append('productType', productType);
    }

    if (stockStatus) {
      params.append('stockStatus', stockStatus);
    }

    if (wave && wave !== 'all') {
      params.append('wave', wave);
    }

    if (brandIds) {
      params.append('brandIds', brandIds);
    }

    if (categoryIds) {
      params.append('categoryIds', categoryIds);
    }

    if (cpProductStatus) {
      params.append('cpProductStatus', cpProductStatus);
    }

    if (cpLastScrapedAt) {
      params.append('cpLastScrapedAt', cpLastScrapedAt);
    }

    if (name) {
      params.append('name', name);
    }

    if (gpi) {
      params.append('gpi', gpi);
    }

    const url = `${this.baseUrl}/products/catalog/all?${params.toString()}`;
    console.info('🌐 API Request:', url);

    const response = await axios.get<ProductsResponse>(url);
    return response.data;
  }

  async getProductById(productId: string): Promise<SkinSeoulAdminProduct> {
    const url = `${this.baseUrl}/products/${productId}`;
    console.info('🌐 API Request:', url);

    const response = await axios.get<SkinSeoulAdminProduct>(url);
    return response.data;
  }

  async getProductsBySku(skus: string[], publicationStatus?: PublicationStatus): Promise<SkinSeoulAdminProduct[]> {
    const params = new URLSearchParams();
    params.append('skus', skus.join(','));

    if (publicationStatus) {
      params.append('publicationStatus', publicationStatus);
    }

    const url = `${this.baseUrl}/products/admin?${params.toString()}`;
    console.info('🌐 API Request:', url);

    const response = await axios.get<SkinSeoulAdminProduct[]>(url);
    return response.data;
  }

  async searchProductsBySku(sku: string, postStatus?: PublicationStatus): Promise<SkinSeoulAdminProduct[]> {
    const params = new URLSearchParams();
    params.append('skus', sku);

    if (postStatus) {
      params.append('publicationStatus', postStatus);
    }

    const url = `${this.baseUrl}/products/admin?${params.toString()}`;
    console.info('🌐 API Request (SKU Search):', url);

    const response = await axios.get<SkinSeoulAdminProduct[]>(url);
    return response.data;
  }

  async getAllCategories(): Promise<CategoryTerm[]> {
    const url = `${this.baseUrl}/product/taxonomy/categories/all`;
    console.info('🌐 API Request (Categories):', url);

    const response = await axios.get<CategoryTerm[]>(url);
    return response.data;
  }

  async getAllBrands(): Promise<BrandTerm[]> {
    const url = `${this.baseUrl}/product/taxonomy/brands`;
    console.info('🌐 API Request (Brands):', url);

    const response = await axios.get<BrandTerm[]>(url);
    return response.data;
  }

  async updateProducts(products: any[]): Promise<{ success: boolean; message?: string }> {
    const url = `${this.baseUrl}/products/admin`;
    console.info('🔄 Updating products:', products);

    try {
      const response = await axios.patch(
        url,
        { products },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      console.info('✅ Products updated successfully');
      return {
        success: true,
        message: response.data?.message || 'Products updated successfully',
      };
    } catch (error) {
      console.error('❌ Failed to update products:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

export const skinSeoulProductsClient = new SkinSeoulProductsClient();
